import {makeObservable, action, observable, computed} from 'mobx';

import {StatusItem, StatusItemStatus} from './StatusItem';

export interface StatusItemStoreParams<
	D extends object = object,
	S extends string = StatusItemStatus,
	T extends StatusItem<D, S> = StatusItem<D, S>,
> {}

export const normalizeStatusItemsOrIds = <
	D extends object = object,
	S extends string = StatusItemStatus,
	T extends StatusItem<D, S> = StatusItem<D, S>,
>(
	itemsOrIds: T | T['id'] | Array<T | T['id']>,
): Array<T | T['id']> => {
	return Array.isArray(itemsOrIds) ? itemsOrIds : [itemsOrIds];
};

export class StatusItemStore<
	D extends object = object,
	S extends string = StatusItemStatus,
	T extends StatusItem<D, S> = StatusItem<D, S>,
> {
	// ItemClass: new (...args: ConstructorParameters<typeof StatusItem>) => T;// = StatusItem as unknown as T;

	protected disposers: Array<() => void> = [];
	items = observable.array<T>([], {deep: true});

	get itemIdsInProgress() {
		return this.items.filter(({complete}) => !complete).map((item) => item.id);
	}

	get viableItems() {
		return this.items.filter(({viable}) => viable);
	}
	get viableItemCount() {
		return this.viableItems.length;
	}
	get hasViableItems() {
		return !this.viableItemCount;
	}

	//////////////////////////////////////////////////////////
	// ACTIVE/INACTIVE/COMPLETED COMPUTES
	//////////////////////////////////////////////////////////
	/** items that could be marked as completed at some point still in the future */
	get activeItems() {
		return this.items.filter((item) => !item.paused && item.viable);
	}
	get activeCount() {
		return this.activeItems.length;
	}
	get hasActiveItems() {
		return this.activeCount > 0;
	}

	/** items that are completed */
	get completedCount() {
		return this.completedItems.length;
	}
	get completedItems() {
		return this.getByCompleted(true);
	}
	get hasCompletedItems() {
		return this.completedCount > 0;
	}
	//////////////////////////////////////////////////////////
	// !ACTIVE/INACTIVE/COMPLETED COMPUTES
	//////////////////////////////////////////////////////////

	get missingCount() {
		return this.missingItems.length;
	}

	get hasMissingItems() {
		return this.missingCount > 0;
	}

	get missingItems() {
		return this.items.filter((item) => item.missing);
	}

	//////////////////////////////////////////////////////////
	// PAUSE COMPUTES
	//////////////////////////////////////////////////////////
	get pausableItems() {
		return this.items.filter(({canPause}) => canPause);
	}
	get pausableCount() {
		return this.pausableItems.length;
	}
	get hasPausableItems() {
		return this.pausableCount > 0;
	}

	get pausedItems() {
		return this.getByPaused(true);
	}
	get pausedCount() {
		return this.pausedItems.length;
	}
	get hasPausedItems() {
		return this.pausedCount > 0;
	}
	get allItemsArePaused() {
		return (
			this.pausedCount + this.missingCount >= this.total - this.completedCount
		);
	}
	//////////////////////////////////////////////////////////
	// !PAUSE COMPUTES
	//////////////////////////////////////////////////////////

	//////////////////////////////////////////////////////////
	// PROGRESS COMPUTES
	//////////////////////////////////////////////////////////
	get itemsInProgressCount() {
		return this.itemIdsInProgress.length;
	}

	get itemsInProgress() {
		return !!this.itemsInProgressCount;
	}

	get inProgress() {
		return !!this.itemsInProgressCount && this.progress !== 100;
	}

	get progress() {
		const items = this.items;
		const totalInProgress = items.length;

		const totalProgress = !this.itemsInProgressCount
			? 100
			: items.reduce((accum, file) => accum + file.progress, 0);

		const progress = Math.floor(
			(totalProgress / (totalInProgress * 100)) * 100,
		);

		return isNaN(progress) ? 0 : progress;
	}
	//////////////////////////////////////////////////////////
	// !PROGRESS COMPUTES
	//////////////////////////////////////////////////////////

	get total() {
		return this.items.length;
	}

	get isEmpty() {
		return !this.total;
	}

	constructor(opts: StatusItemStoreParams<D, S, T> = {}) {
		makeObservable(
			this,
			{
				activeCount: computed,
				activeItems: computed,
				allItemsArePaused: computed,
				addItems: action,
				completedCount: computed,
				completedItems: computed,
				hasActiveItems: computed,
				hasCompletedItems: computed,
				hasMissingItems: computed,
				hasPausableItems: computed,
				hasPausedItems: computed,
				hasViableItems: computed,
				inProgress: computed,
				isEmpty: computed,
				itemsInProgress: computed,
				itemsInProgressCount: computed,
				itemIdsInProgress: computed,
				items: observable,
				missingCount: computed,
				missingItems: computed,
				progress: computed,
				pausableCount: computed,
				pausableItems: computed,
				pauseAll: action,
				pausedCount: computed,
				pausedItems: computed,
				removeItems: action,
				removePaused: action,
				resumeAll: action,
				resumeItems: action,
				setComplete: action,
				setItems: action,
				total: computed,
				viableItems: computed,
				viableItemCount: computed,
			},
			{
				deep: true,
			},
		);
	}

	/** get item by id */
	getById(id?: T['id']) {
		return id != null ? this.items.find(({id: _id}) => _id === id) : undefined;
	}

	/** get item index by id */
	getIndexById(id?: T['id']) {
		return id != null ? this.items.findIndex(({id: _id}) => _id === id) : -1;
	}

	getByCompleted(completed: boolean) {
		return this.items.filter(({complete}) => completed === complete);
	}

	getByStatus(status: S) {
		return this.items.filter(({status: _status}) => _status === status);
	}

	getByPaused(paused: boolean) {
		return this.items.filter(
			({paused: _paused, missing}) => _paused === paused && !missing,
		);
	}

	getByInStatuses(statuses: S[]) {
		return this.items.filter((item) => item.isInStatuses(statuses));
	}

	getByNotInStatuses(statuses: S[]) {
		return this.items.filter((item) => item.notInStatuses(statuses));
	}

	/** add item(s) */
	addItems(items: T | T[], toBeginning = false) {
		items = Array.isArray(items) ? items : [items];
		toBeginning ? this.items.unshift(...items) : this.items.push(...items);
		return items;
	}

	/**
	 * pause all items
	 * @returns Paused items
	 */
	pauseAll() {
		return this.pauseItems(this.items);
	}

	/**
	 * pause item(s)
	 * itemsOrIds Items to pause (can be item instance or id)
	 * @returns Paused items
	 */
	pauseItems(itemsOrIds: T | T['id'] | T[] | T['id'][]) {
		return normalizeStatusItemsOrIds<D, S, T>(itemsOrIds).reduce<T[]>(
			(accum, itemOrId) => {
				const item =
					typeof itemOrId === 'string' ? this.getById(itemOrId) : itemOrId;
				if (!item?.setPaused(true)) return accum;
				return [...accum, item];
			},
			[],
		);
	}

	/**
	 * remove `inactive` items
	 * @returns Removed items
	 */
	removeInactive() {
		return this.removeItems(this.completedItems);
	}

	/**
	 * remove `missing` items (files)
	 * @returns Removed items
	 */
	removeMissing() {
		return this.removeItems(this.missingItems);
	}

	/**
	 * remove item(s)
	 * itemsOrIds Items to remove (can be item instance or id)
	 * @returns Removed items
	 */
	removeItems(itemsOrIds: T | T['id'] | Array<T | T['id']>) {
		return normalizeStatusItemsOrIds<D, S, T>(itemsOrIds).reduce<T[]>(
			(accum, itemOrId) => {
				const item =
					typeof itemOrId === 'string' ? this.getById(itemOrId) : itemOrId;
				if (item && this.items.remove(item)) {
					item.dispose();
					return [...accum, item];
				}
				return accum;
			},
			[],
		);
	}

	removeItemsByStatus(status: S) {
		return this.removeItems(this.getByStatus(status));
	}

	removePaused() {
		return this.removeItems(this.getByPaused(true));
	}

	/** resume all items */
	resumeAll() {
		return this.resumeItems(this.items);
	}

	resumeItems(items: T | T['id'] | Array<T | T['id']>) {
		return normalizeStatusItemsOrIds<D, S, T>(items).reduce<T[]>(
			(accum, itemOrId) => {
				const item =
					typeof itemOrId === 'string' ? this.getById(itemOrId) : itemOrId;
				return item?.setPaused(false) ? [...accum, item] : accum;
			},
			[],
		);
	}

	setComplete(items: T | T['id'] | Array<T | T['id']>, completed: boolean) {
		normalizeStatusItemsOrIds<D, S, T>(items).forEach((itemOrId) => {
			const item =
				typeof itemOrId === 'string' ? this.getById(itemOrId) : itemOrId;
			item?.setComplete(completed);
		});
	}

	/** replace items */
	setItems(items: T[]) {
		this.removeItems([...this.items]);
		this.addItems(items);
	}

	/** set status for multiple items */
	setItemsStatus(itemsOrIds: T | T['id'] | Array<T | T['id']>, status: S) {
		normalizeStatusItemsOrIds<D, S, T>(itemsOrIds).forEach((itemOrId) => {
			(typeof itemOrId === 'string'
				? this.getById(itemOrId)
				: itemOrId
			)?.setStatus(status);
		});
	}

	/** toggle item's paused state */
	// TODO: rename toggleItemPause
	toggleItemPause(id: string) {
		const item = this.getById(id);
		if (!item) return;
		if (item.canPause) {
			this.pauseItems(item);
			return true;
		}
		this.resumeItems(item);
		return false;
	}

	toJson() {
		return this.items.map((item) => item.toJson());
	}

	/** cleanup */
	dispose() {
		this.items.forEach((item) => item.dispose());
		// this.items.clear();
		this.disposers.forEach((disposer) => disposer());
	}
}

export default StatusItemStore;
